import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSwipeGesture } from '../../hooks/useSwipeGesture';
import LanguageSwitcher from '../common/LanguageSwitcher';
import './MobileNavigation.css';

interface MobileNavigationProps {
  theme: 'light' | 'dark';
  onThemeToggle: () => void;
}

const MobileNavigation: React.FC<MobileNavigationProps> = ({ theme, onThemeToggle }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const menuRef = useRef<HTMLDivElement>(null);
  
  // Swipe gesture to close menu
  useSwipeGesture(menuRef, {
    onSwipeRight: () => setIsOpen(false),
  });

  // Close menu on route change
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const handleNavClick = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const menuItems = [
    { path: '/', label: t('nav.home'), icon: '🏠', description: t('nav.homeDescription', 'Learn about our platform') },
    { path: '/pricing', label: t('nav.pricing'), icon: '💰', description: t('nav.pricingDescription', 'View pricing plans') },
    { path: '/lookup', label: t('nav.caseLookup'), icon: '📁', description: t('nav.caseLookupDescription', 'Look up your case') },
    { path: '/auth/login', label: t('nav.login'), icon: '👤', description: t('nav.adminDescription', 'Admin dashboard') },
  ];

  const isActive = (path: string) => {
    if (path === '/' && location.pathname === '/') return true;
    if (path !== '/' && location.pathname.startsWith(path)) return true;
    return false;
  };

  return (
    <>
      {/* Mobile Header */}
      <div className="mobile-header">
        <div className="mobile-header-content">
          <div className="mobile-logo" onClick={() => navigate('/')}>
            <img 
              src={theme === 'dark' ? "/wp-high-resolution-logo-darkmode.svg" : "/wp-high-resolution-logo-transparent.svg"} 
              alt="WhistlePro" 
              className={`mobile-logo ${logoLoaded ? 'loaded' : 'loading'}`}
              onLoad={() => setLogoLoaded(true)}
            />
          </div>
          
          <div className="mobile-header-actions">
            <LanguageSwitcher />
            
            <button
              className="mobile-theme-toggle"
              onClick={onThemeToggle}
              aria-label={`Switch to ${theme === 'light' ? 'dark' : 'light'} mode`}
            >
              {theme === 'light' ? '🌙' : '☀️'}
            </button>
            
            <button
              className={`mobile-menu-toggle ${isOpen ? 'open' : ''}`}
              onClick={toggleMenu}
              aria-label="Toggle menu"
              aria-expanded={isOpen}
            >
              <span className="mobile-menu-icon">
                <span></span>
                <span></span>
                <span></span>
              </span>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <div 
        className={`mobile-menu-overlay ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(false)}
      />

      {/* Mobile Menu */}
      <nav ref={menuRef} className={`mobile-menu ${isOpen ? 'open' : ''}`}>
        <div className="mobile-menu-content">
          <div className="mobile-menu-items">
            {menuItems.map((item) => (
              <button
                key={item.path}
                className={`mobile-menu-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => handleNavClick(item.path)}
              >
                <span className="mobile-menu-icon">{item.icon}</span>
                <div className="mobile-menu-text">
                  <span className="mobile-menu-label">{item.label}</span>
                  <span className="mobile-menu-description">{item.description}</span>
                </div>
                {isActive(item.path) && <span className="mobile-menu-indicator" />}
              </button>
            ))}
          </div>

          <div className="mobile-menu-footer">
            <button
              className="mobile-get-started"
              onClick={() => handleNavClick('/pricing')}
            >
              {t('nav.getStarted')} →
            </button>
          </div>
        </div>
      </nav>
    </>
  );
};

export default MobileNavigation;